"use client";

import { type SignInFormSchema } from "@/components/blocks/AuthModal.data";
import { checkout as checkoutApi, login, logout } from "@/lib/api";
import { signInWithCredentials, signInWithGoogle, signup } from "@/lib/auth";
import { type Cart } from "@/lib/cart";
import { upsertCookie } from "@/lib/cookies";
import { createStore } from "@/lib/store";
import { type Product, type User } from "@repo/schemas";
import { CART_COOKIE_NAME, type CartCookie } from "./cookies";

export type GlobalStore = {
  isAuthModalOpened: boolean;
  user: User | null;
  cart: Cart;
  isCartPopupOpened: boolean;
};

export const {
  StoreProvider: GlobalProvider,
  StoreConsumer: GlobalConsumer,
  useGetters: useGlobalGetters,
  useSetters: useGlobalSetters,
  useMutations: useGlobalMutations,
} = createStore<GlobalStore>()
  .addGetters({
    isAuthModalOpened: (store) => store.isAuthModalOpened,
    isLoggedIn: (store) => !!store.user,
    nbOfCartItems: (store) => store.cart.items.length,
    cartItems: (store) => store.cart.items,
    user: (store) => store.user,
    isCartPopupOpened: (store) => store.isCartPopupOpened,
    all: (s) => s,
  })
  .addSetters({
    setUser: (store, user: GlobalStore["user"]) => {
      store.user = user;
    },
    setIsAuthModalOpened: (store, value: boolean) => {
      store.isAuthModalOpened = value;
    },
    setIsCartPopupOpened(store, isOpened: boolean) {
      store.isCartPopupOpened = isOpened;
    },
    _addProductToCart(store, product: Product) {
      const itemIndex = store.cart.items.findIndex((item) => item.product.id === product.id);
      if (itemIndex === -1) {
        store.cart.items.push({ product, quantity: 1 });
      } else {
        store.cart.items[itemIndex].quantity += 1;
      }
      store.isCartPopupOpened = true;
    },
    _removeProductFromCart(store, productId: string) {
      const itemIndex = store.cart.items.findIndex((item) => item.product.id === productId);
      if (itemIndex === -1) {
        return;
      }
      store.cart.items.splice(itemIndex, 1);
    },
    _decrementProductQuantity(store, productId: string) {
      const itemIndex = store.cart.items.findIndex((item) => item.product.id === productId);
      if (itemIndex === -1) {
        return;
      }
      if (store.cart.items[itemIndex].quantity > 1) {
        store.cart.items[itemIndex].quantity -= 1;
      } else {
        store.cart.items.splice(itemIndex, 1);
      }
    },
    _clearCart(store) {
      store.cart.items = [];
    },
  })
  .addMutations({
    async login(
      store,
      payload:
        | { type: "credentials"; credentials: SignInFormSchema }
        | { type: "signup"; credentials: SignInFormSchema }
        | { type: "google" },
    ) {
      let idToken: string;

      switch (payload.type) {
        case "credentials": {
          idToken = await signInWithCredentials(payload.credentials);
          break;
        }
        case "signup": {
          idToken = await signup(payload.credentials);
          break;
        }
        case "google": {
          idToken = await signInWithGoogle();
          break;
        }
      }

      const { user } = await login({ idToken });
      store.setUser(user);
      store.setIsAuthModalOpened(false);
    },
    async logout(store) {
      await logout();
      store.setUser(null);
    },
    async addToCart(store, product: Product) {
      store._addProductToCart(product);
      upsertCookie<CartCookie>(CART_COOKIE_NAME, storeCartToCookieCart(store.get().cart));
    },
    async removeFromCart(store, productId: string) {
      store._removeProductFromCart(productId);
      upsertCookie<CartCookie>(CART_COOKIE_NAME, storeCartToCookieCart(store.get().cart));
    },
    async decrementProductQuantity(store, productId: string) {
      store._decrementProductQuantity(productId);
      upsertCookie<CartCookie>(CART_COOKIE_NAME, storeCartToCookieCart(store.get().cart));
    },
    async checkout(store) {
      const checkoutUrl = await checkoutApi(storeCartToCookieCart(store.get().cart));
      window.location.href = checkoutUrl;
    },
    async clearCart(store) {
      store._clearCart();
      upsertCookie<CartCookie>(CART_COOKIE_NAME, { productIds: [] });
    },
  });

function storeCartToCookieCart(cart: Cart): CartCookie {
  return {
    productIds: cart.items.flatMap((p) => Array(p.quantity).fill(p.product.id)),
  };
}
